import { Link } from "react-router-dom";
import { Phone, ChevronDown, Wrench } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import BurgerMenu from "./BurgerMenu";

const NAV_SERVICES = [
  ["/services/emergency-plumbing", "Emergency Plumbing"],
  ["/services/shower-head-repair", "Shower Head Repair"],
  ["/services/water-heater-services", "Water Heater Services"],
  ["/services/leak-detection-repair", "Leak Detection & Repair"],
  ["/services/toilet-fixture-repair", "Toilet & Fixture Repair"],
];

const NAV_LOCATIONS = [
  ["/locations/lincoln-park", "Lincoln Park"],
  ["/locations/wicker-park", "Wicker Park"],
  ["/locations/lakeview", "Lakeview"],
  ["/locations/logan-square", "Logan Square"],
  ["/locations/river-north", "River North"],
  ["/locations/south-loop", "South Loop"],
  ["/locations/old-town", "Old Town"],
];

const stickyNavStyle = {
  background: "rgba(15, 23, 42, 0.96)",
  backdropFilter: "blur(12px)",
};

function DropdownPanel({ items, linkTo, linkLabel }) {
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 z-50 navbar-popups">
      <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-5">
        {items.map(([to, label]) => (
          <li key={to}>
            <Link to={to} className="text-gray-600 text-sm font-medium hover:text-blue-600 transition-colors block leading-snug">
              {label}
            </Link>
          </li>
        ))}
      </ul>
      <Link to={linkTo} className="w-full block text-center text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors px-4 py-2.5 rounded-xl">
        {linkLabel} &rarr;
      </Link>
    </div>
  );
}

function NavDropdownTrigger({ label, open, onClick }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1 font-semibold text-white/80 hover:text-white transition-colors cursor-pointer text-sm navbar-links">
      {label}
      <ChevronDown className="w-4 h-4 transition-transform duration-200" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
    </button>
  );
}

export default function StickyNav({ upSectionRef }) {
  const [menuOpen, setMenuOpen] = useState(null);
  const navRef = useRef(null);
  const tabNavRef = useRef(null);
  const mNavRef = useRef(null);

  const toggle = (type) => setMenuOpen((prev) => (prev === type ? null : type));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target) && !e.target.closest(".navbar-popups")) setMenuOpen(null);
      if (tabNavRef.current && !tabNavRef.current.contains(e.target) && !e.target.closest(".navbar-popups")) setMenuOpen(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!upSectionRef?.current) return;
    const threshold = upSectionRef.current.offsetTop;
    const onScroll = () => {
      const past = window.scrollY >= threshold;
      [navRef, tabNavRef, mNavRef].forEach((ref) => {
        if (!ref.current) return;
        ref.current.classList.toggle("popup", past);
        ref.current.classList.toggle("-translate-y-full", !past);
      });
      if (!past) setMenuOpen(null);
    };
    document.addEventListener("scroll", onScroll, { passive: true });
    return () => document.removeEventListener("scroll", onScroll);
  }, [upSectionRef]);

  return (
    <>
      {/* Sticky desktop navbar */}
      <nav id="navbar" ref={navRef} className="hidden lg:block fixed top-0 left-0 w-full -translate-y-full z-50" style={stickyNavStyle}>
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center">
              <Wrench className="w-3.5 h-3.5 text-white" />
            </span>
            <span className="font-black text-white text-lg tracking-tight">Chicago Plumbing</span>
          </Link>
          <div className="flex items-center gap-7">
            <div className="relative">
              <NavDropdownTrigger label="Services" open={menuOpen === "services"} onClick={() => toggle("services")} />
              {menuOpen === "services" && <DropdownPanel items={NAV_SERVICES} linkTo="/services" linkLabel="All Services" />}
            </div>
            <div className="relative">
              <NavDropdownTrigger label="Locations" open={menuOpen === "locations"} onClick={() => toggle("locations")} />
              {menuOpen === "locations" && <DropdownPanel items={NAV_LOCATIONS} linkTo="/locations" linkLabel="All Locations" />}
            </div>
            <a href="tel:(312) 555-0147" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 transition-colors text-white font-bold px-4 py-2.5 rounded-xl text-sm">
              <Phone className="w-4 h-4" /> (312) 555-0147
            </a>
          </div>
        </div>
      </nav>

      {/* Sticky tablet navbar */}
      <nav ref={tabNavRef} className="hidden md:flex lg:hidden fixed top-0 left-0 w-full -translate-y-full z-50 items-center justify-between px-8 py-5" style={stickyNavStyle}>
        <span className="font-black text-white text-lg tracking-tight">Chicago Plumbing</span>
        <div className="flex items-center gap-6">
          <div className="relative">
            <NavDropdownTrigger label="Services" open={menuOpen === "services"} onClick={() => toggle("services")} />
            {menuOpen === "services" && <DropdownPanel items={NAV_SERVICES} linkTo="/services" linkLabel="All Services" />}
          </div>
          <div className="relative">
            <NavDropdownTrigger label="Locations" open={menuOpen === "locations"} onClick={() => toggle("locations")} />
            {menuOpen === "locations" && <DropdownPanel items={NAV_LOCATIONS} linkTo="/locations" linkLabel="All Locations" />}
          </div>
          <a href="tel:(312) 555-0147" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-colors">
            <Phone className="w-4 h-4" /> Call Us
          </a>
        </div>
      </nav>

     {/* Sticky mobile navbar */}
<nav ref={mNavRef} className={`md:hidden fixed left-0 w-full -translate-y-full z-50 top-0 px-4 py-4`} style={stickyNavStyle}>
  <div className="flex items-center justify-between mb-3 mt-2">
    <Link to="/" className="flex items-center gap-2">
      <span className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
        <Wrench className="w-3.5 h-3.5 text-white" />
      </span>
      <span className="font-black text-white text-base tracking-tight">Chicago Plumbing</span>
    </Link>
    <a href="tel:(312) 555-0147" className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 transition-colors text-white font-bold px-4 py-2 rounded-xl text-sm">
      <Phone className="w-3.5 h-3.5" /> Call Now
    </a>
  </div>
  <div className="flex items-center gap-2">
    <Link to="/services" className="flex-1 text-center text-xs font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/20 transition-colors py-2 rounded-lg">
      Services
    </Link>
    <Link to="/locations" className="flex-1 text-center text-xs font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/20 transition-colors py-2 rounded-lg">
      Locations
    </Link>
    <a href="#faqs" className="flex-1 text-center text-xs font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/20 transition-colors py-2 rounded-lg">
      FAQs
    </a>
    <a href="#footer" className="flex-1 text-center text-xs font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/20 transition-colors py-2 rounded-lg">
      Contact
    </a>
  </div>
</nav>
    </>
  );
}